import { Component, OnDestroy, OnInit, ViewChildren } from '@angular/core';
import { FormGroup, UntypedFormArray } from '@angular/forms';
import { Router } from '@angular/router';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { EstateDao } from '@app/dao/estate/estate.dao';
import { ProductDao } from '@app/dao/product/product.dao';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { UserDao } from '@app/dao/user/user.dao';
import * as _ from 'lodash';
import moment from 'moment';
import { BehaviorSubject, combineLatest, Subject, takeWhile } from 'rxjs';
import { EaFeesFormComponent } from '../ea-fees-form/ea-fees-form.component';
import { EaLmnpLmpFormService } from './ea-lmnplmp-form.service';

@Component({
  selector: 'ea-lmnplmp-form',
  templateUrl: './ea-lmnplmp-form.component.html',
  styleUrls: ['./ea-lmnplmp-form.component.scss'],
  standalone: false,
})
export class EaLmnplmpFormComponent
  extends PrFormAbstract
  implements OnInit, OnDestroy
{
  users: any[];
  countries: any[] = [];
  fiscalLaws: any[] = [];
  conversionLaws: any[] = [];
  conversionFiscalLaw: string;
  converting = false;
  lawPartners: any[] = [];
  subscriptionSource: any[] = [];
  tabChanged: BehaviorSubject<any> = new BehaviorSubject(null);
  changeTab: BehaviorSubject<any> = new BehaviorSubject(null);
  doneSubject: Subject<void> = new Subject<void>();
  alive = true;
  isGMaps: boolean = true;
  @ViewChildren(EaFeesFormComponent) feesFormsComponents: EaFeesFormComponent;

  constructor(
    protected formConstructor: PrFormConstructor,
    private userDao: UserDao,
    private estateDao: EstateDao,
    private referenceDao: ReferenceDao,
    private eaLmnpLmpFormService: EaLmnpLmpFormService,
    private productDao: ProductDao,
    private router: Router,
  ) {
    super(formConstructor);
  }

  get title() {
    if (this.entity.rentalType === 'UNFURNISHED')
      return this.generateTitle(
        'Modifier la location nue',
        'Ajouter une location nue',
      );
    else
      return this.generateTitle(
        'Modifier la location meublée',
        'Ajouter une location meublée',
      );
  }

  generateTitle(editTitle: string, addTitle: string) {
    if (!this.entity || !this.entity.id) return addTitle;
    if (!this.entity.label || this.entity.label === '') return editTitle;
    return `${editTitle} “${this.entity.label}”`;
  }

  ngOnInit() {
    combineLatest([
      this.userDao.getCouple(),
      this.referenceDao.getCountries(),
      this.referenceDao.getFiscalLaw(),
      this.referenceDao.getSubscriptionSource(),
    ])
      .pipe(takeWhile(() => this.alive))
      .subscribe(([users, countries, fiscalLaws, subscriptionSource]) => {
        let group = fiscalLaws.find(
          g =>
            g.typeProduct.toLowerCase() === this.entity.typeUrl.toLowerCase(),
        );
        if (!group) group = fiscalLaws.find(g => g.typeProduct === 'LMNP');
        group.laws.sort((a, b) => a.key.localeCompare(b.key));
        this.users = users;
        this.countries = countries;
        this.fiscalLaws = group.laws;
        this.subscriptionSource = subscriptionSource;
        this.initConversionLaws(fiscalLaws);

        this.form
          .get('fiscalLaw.startDate')
          .valueChanges.pipe(takeWhile(() => this.alive))
          .subscribe(startDate => {
            if (
              !this.form.get('realestate.startDate').value ||
              moment(startDate).isBefore(
                this.form.get('realestate.startDate').value,
              )
            )
              this.form
                .get('realestate.startDate')
                .setValue(startDate, { emitEvent: false });
          });

        this.form
          .get('realestate.startDate')
          .valueChanges.pipe(takeWhile(() => this.alive))
          .subscribe(startDate => {
            if (
              !this.form.get('fiscalLaw.startDate').value ||
              moment(startDate).isAfter(
                this.form.get('fiscalLaw.startDate').value,
              )
            )
              this.form.get('fiscalLaw.startDate').setValue(startDate);
          });

        this.form
          .get('loans')
          .get('hasLoan')
          .valueChanges.pipe(takeWhile(() => this.alive))
          .subscribe(hasLoan =>
            hasLoan ? this.patchValueToParentValues() : undefined,
          );

        this.form
          .get('fiscalLaw.type')
          .valueChanges.pipe(takeWhile(() => this.alive))
          .subscribe(law => {
            if (!law) return;
            this.lawPartners = [];
            this.form.get('fiscalLaw.subscriptionPartner').reset();
            this.productDao
              .getProviders({ productType: this.entity?.rentalType || 'LMNP' })
              .pipe(takeWhile(() => this.alive))
              .subscribe(partners => {
                this.lawPartners = partners;
                if (this.form.get('fiscalLaw.subscriptionPartner').value)
                  this.form.controls.fiscalLaw.patchValue({
                    subscriptionPartner: this.lawPartners.find(
                      l =>
                        l.id ==
                        this.form.get('fiscalLaw.subscriptionPartner').value,
                    ),
                  });
              });
          });

        if (!this.entity.fiscalLaw || this.entity.fiscalLaw.type == null) {
          this.entity.fiscalLaw = { type: this.fiscalLaws[0].id };
          this.form.controls.fiscalLaw.patchValue({
            type: this.fiscalLaws[0].id,
          });
        } else {
          this.form.controls.fiscalLaw.patchValue({
            type: this.fiscalLaws.find(
              l => l.key == this.entity.fiscalLaw.type,
            ),
            subscriptionPartner: this.entity.subscriptionPartner,
          });
        }
      });
  }

  get targetType() {
    return this.entity?.typeUrl === 'unfurnished' ? 'LMNP' : 'UNFURNISHED';
  }

  get currentTypeLabel() {
    return this.entity?.typeUrl === 'unfurnished'
      ? 'location nue'
      : 'location meublée';
  }

  get targetTypeLabel() {
    return this.entity?.typeUrl === 'unfurnished'
      ? 'location meublée'
      : 'location nue';
  }

  get canConvertType() {
    return (
      !!this.entity?.id &&
      !!this.entity?.userId &&
      !['PENELOP', 'BUDGET_INSIGHT'].includes(this.entity?.origin)
    );
  }

  initConversionLaws(fiscalLawGroups: any[]) {
    const targetProduct = this.targetType === 'LMNP' ? 'lmnp' : 'unfurnished';
    let group = fiscalLawGroups.find(
      g => g.typeProduct.toLowerCase() === targetProduct,
    );
    if (!group) group = fiscalLawGroups.find(g => g.typeProduct === 'LMNP');
    this.conversionLaws = group
      ? [...group.laws].sort((a, b) => a.key.localeCompare(b.key))
      : [];

    const preferred = this.targetType === 'LMNP' ? 'BIC' : 'FONCIER';
    this.conversionFiscalLaw = (
      this.conversionLaws.find(law => law.key === preferred) ||
      this.conversionLaws[0]
    )?.key;
  }

  convertType() {
    if (this.converting) return;
    this.converting = true;
    this.prFormService.loading();
    this.estateDao
      .convertRentalType(this.entity.userId, this.entity.typeUrl, this.entity.id, {
        targetType: this.targetType,
        targetFiscalLaw: this.conversionFiscalLaw,
      })
      .subscribe(
        res => {
          this.converting = false;
          this.prFormService.loaded();
          this.notificationService.success('Type de bien modifié');
          this.doneSubject.next();
          const result = res?.data;
          if (result?.id) {
            const typeUrl =
              result.targetType === 'UNFURNISHED' ? 'unfurnished' : 'lmplmnp';
            this.router.navigate([
              '/dashboard/estates',
              typeUrl,
              'detail',
              result.id,
            ]);
          } else {
            this.router.navigate(['/dashboard/estates/overview']);
          }
        },
        errors => {
          console.error(errors);
          this.converting = false;
          this.prFormService.loaded();
          this.notificationService.error('Le changement de type a échoué');
        },
      );
  }

  patchValueToParentValues() {
    let estateForm = this.form.get('realestate').value;
    if (!estateForm) return;

    let loanForm = this.form.get('loans');
    loanForm.get('label').setValue(estateForm.label);
    loanForm.get('amountBorrowed').setValue(estateForm.purchasePrice);
    loanForm.get('startDate').setValue(estateForm.startDate);
  }

  onSubmit() {
    this.displayErrors();

    if (this.form.invalid) {
      const realestateGroup = this.form.get('realestate') as FormGroup;
      if (realestateGroup.controls.address.status == 'INVALID') {
        this.isGMaps = false;
      }

      this.changeTab.next('FIRST');
      return;
    }

    const extract = this.extract();
    let formDTOExtraction = _.merge(this.entity, extract);
    if (!extract.loans && formDTOExtraction.loans)
      formDTOExtraction.removeLoan = true;

    formDTOExtraction.estimations = extract.estimations;
    this.prFormService.loading();

    this.estateDao.postEstate(formDTOExtraction).subscribe(
      success => {
        this.prFormService.loaded();
        this.doneSubject.next();
        this.notificationService.success('La sauvegarde a réussi');
      },
      errors => {
        console.error(errors);
        this.prFormService.loaded();
        this.notificationService.error('La sauvegarde a échoué');
      },
    );
  }

  /* public generate() {
    return this.eaLmnpLmpFormService.generate(this.entity);
  } */

  public generate = () => this.eaLmnpLmpFormService.generate(this.entity);
  public extract = () => this.eaLmnpLmpFormService.extract(this.form);

  durationLabel(type) {
    if (['MALRAUX', 'MALRAUX_SECTEUR_SAUVE', 'MONUMENT_HISTO'].includes(type))
      return 'Durée des travaux de rénovation (MH/Malraux)';
    else if (['PINEL', 'PINEL_DOMTOM'].includes(type))
      return "Durée d'engagement (Pinel)";
  }

  compareFn(a, b) {
    if (a === b) return true;
  }

  get fees() {
    return this.form.get('fees') as UntypedFormArray;
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  onTabChange(isLastTab: boolean) {
    this.tabChanged.next(isLastTab);
  }

  nextTab() {
    this.changeTab.next('NEXT');
  }
}
